import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  FileText, Mail, Phone, Bell, Newspaper, Download, RefreshCw, 
  Search, Filter, Eye, Archive, CheckCircle, Clock, AlertCircle,
  Building, User, MessageSquare, Calendar, ExternalLink
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const REQUEST_TYPES = {
  quote: { label: 'Devis', icon: FileText, color: 'bg-blue-500' },
  contact: { label: 'Contact', icon: Mail, color: 'bg-emerald-500' },
  callback: { label: 'Rappel', icon: Phone, color: 'bg-amber-500' },
  newsletter: { label: 'Newsletter', icon: Newspaper, color: 'bg-purple-500' },
  brochure: { label: 'Brochure', icon: Download, color: 'bg-pink-500' }
};

const STATUS_CONFIG = {
  new: { label: 'Nouveau', icon: AlertCircle, color: 'bg-red-500', textColor: 'text-red-600' },
  in_progress: { label: 'En cours', icon: Clock, color: 'bg-amber-500', textColor: 'text-amber-600' },
  completed: { label: 'Traité', icon: CheckCircle, color: 'bg-emerald-500', textColor: 'text-emerald-600' },
  archived: { label: 'Archivé', icon: Archive, color: 'bg-slate-400', textColor: 'text-slate-500' }
};

const CMSRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewModal, setViewModal] = useState({ open: false, request: null });

  useEffect(() => { loadData(); }, [filterType, filterStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterType) params.append('request_type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      
      const [requestsRes, statsRes] = await Promise.all([
        api.get(`/cms/requests?${params.toString()}`),
        api.get('/cms/requests/stats')
      ]);
      
      setRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur de chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      await api.put(`/cms/requests/${requestId}`, { status: newStatus });
      toast.success('Statut mis à jour');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const updateNotes = async (requestId, notes) => {
    try {
      await api.put(`/cms/requests/${requestId}`, { notes });
      toast.success('Notes enregistrées');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const filteredRequests = requests.filter(req => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      req.name?.toLowerCase().includes(term) ||
      req.email?.toLowerCase().includes(term) ||
      req.company?.toLowerCase().includes(term) ||
      req.phone?.includes(term)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Demandes</h1>
          <p className="text-slate-500">Gérez les demandes de devis, contacts et inscriptions</p>
        </div>
        <Button onClick={loadData} variant="outline" className="gap-2">
          <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium">Nouveaux</p>
                  <p className="text-2xl font-bold text-red-700">{stats.new}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 font-medium">En cours</p>
                  <p className="text-2xl font-bold text-amber-700">{stats.in_progress}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600 font-medium">Traités</p>
                  <p className="text-2xl font-bold text-emerald-700">{stats.completed}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Devis</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.by_type?.quote || 0}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Total</p>
                  <p className="text-2xl font-bold text-slate-700">{stats.total}</p>
                </div>
                <Mail className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Rechercher par nom, email, entreprise..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <Filter className="w-4 h-4 text-slate-400" />
              <select 
                className="border rounded-md px-3 py-2 bg-white text-sm"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="">Tous les types</option>
                {Object.entries(REQUEST_TYPES).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
              <select 
                className="border rounded-md px-3 py-2 bg-white text-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">Tous les statuts</option>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {filteredRequests.length} demande{filteredRequests.length > 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucune demande {filterType || filterStatus ? 'correspondant aux filtres' : 'pour le moment'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredRequests.map(req => {
                const typeConfig = REQUEST_TYPES[req.type] || REQUEST_TYPES.contact;
                const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.new;
                const TypeIcon = typeConfig.icon;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <div 
                    key={req.id}
                    className={cn(
                      "border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer",
                      req.status === 'new' && "border-l-4 border-l-red-500 bg-red-50/30"
                    )}
                    onClick={() => setViewModal({ open: true, request: req })}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center text-white", typeConfig.color)}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-800">{req.name || 'Sans nom'}</h3>
                            <Badge variant="secondary" className={cn("text-xs", statusConfig.textColor)}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600">{req.email}</p>
                          {req.company && (
                            <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                              <Building className="w-3 h-3" />
                              {req.company}
                            </p>
                          )}
                          {req.service && (
                            <p className="text-xs text-cyan-600 mt-1">
                              {req.service} {req.solution && `• ${req.solution}`}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-slate-400">{formatDate(req.created_at)}</p>
                        <Badge variant="outline" className="mt-1 text-xs">{typeConfig.label}</Badge>
                      </div>
                    </div>
                    {req.message && (
                      <p className="text-sm text-slate-600 mt-3 line-clamp-2 bg-slate-50 rounded p-2">
                        "{req.message}"
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View/Edit Modal */}
      <Dialog open={viewModal.open} onOpenChange={(open) => !open && setViewModal({ open: false, request: null })}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewModal.request && (
                <>
                  {(() => {
                    const TypeIcon = REQUEST_TYPES[viewModal.request.type]?.icon || Mail;
                    return <TypeIcon className="w-5 h-5 text-cyan-500" />;
                  })()}
                  Demande de {REQUEST_TYPES[viewModal.request?.type]?.label || 'contact'}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {viewModal.request && (
            <div className="space-y-4">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <User className="w-3 h-3" /> Nom
                  </p>
                  <p className="font-medium">{viewModal.request.name || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Building className="w-3 h-3" /> Entreprise
                  </p>
                  <p className="font-medium">{viewModal.request.company || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Email
                  </p>
                  <a href={`mailto:${viewModal.request.email}`} className="font-medium text-cyan-600 hover:underline flex items-center gap-1">
                    {viewModal.request.email}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Téléphone
                  </p>
                  <a href={`tel:${viewModal.request.phone}`} className="font-medium text-cyan-600 hover:underline flex items-center gap-1">
                    {viewModal.request.phone || '-'}
                    {viewModal.request.phone && <ExternalLink className="w-3 h-3" />}
                  </a>
                </div>
              </div>

              {/* Service Info */}
              {(viewModal.request.service || viewModal.request.solution || viewModal.request.hosting) && (
                <div className="bg-cyan-50 rounded-lg p-3">
                  <p className="text-xs text-cyan-600 font-medium mb-2">Demande concernant</p>
                  {viewModal.request.service && <p className="text-sm"><strong>Service :</strong> {viewModal.request.service}</p>}
                  {viewModal.request.solution && <p className="text-sm"><strong>Prestation :</strong> {viewModal.request.solution}</p>}
                  {viewModal.request.hosting && <p className="text-sm"><strong>Hébergement :</strong> {viewModal.request.hosting}</p>}
                </div>
              )}

              {/* Message */}
              {viewModal.request.message && (
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" /> Message
                  </p>
                  <p className="bg-slate-50 rounded-lg p-3 text-sm whitespace-pre-wrap">{viewModal.request.message}</p>
                </div>
              )}

              {/* Metadata */}
              <div className="flex items-center justify-between text-xs text-slate-500 border-t pt-3">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Reçu le {formatDate(viewModal.request.created_at)}
                </span>
                <span>ID: {viewModal.request.id}</span>
              </div>

              {/* Status Change */}
              <div className="space-y-2">
                <p className="text-xs text-slate-500">Changer le statut</p>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(STATUS_CONFIG).filter(([key]) => key !== 'archived').map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <Button
                        key={key}
                        variant={viewModal.request.status === key ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          updateStatus(viewModal.request.id, key);
                          setViewModal({ ...viewModal, request: { ...viewModal.request, status: key } });
                        }}
                        className={cn(viewModal.request.status === key && config.color)}
                      >
                        <Icon className="w-3 h-3 mr-1" />
                        {config.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <p className="text-xs text-slate-500">Notes internes</p>
                <Textarea 
                  defaultValue={viewModal.request.notes || ''}
                  placeholder="Ajouter des notes sur cette demande..."
                  rows={3}
                  onBlur={(e) => {
                    if (e.target.value !== viewModal.request.notes) {
                      updateNotes(viewModal.request.id, e.target.value);
                    }
                  }}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => {
                updateStatus(viewModal.request.id, 'archived');
                setViewModal({ open: false, request: null });
              }}
            >
              <Archive className="w-4 h-4 mr-1" />
              Archiver
            </Button>
            <Button variant="outline" onClick={() => setViewModal({ open: false, request: null })}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSRequestsPage;
